import {
  createFileRoute,
  useNavigate,
  useParams,
} from "@tanstack/react-router";
import { ArrowLeft } from "lucide-react";
import { useEffect, useState } from "react";

import { FollowUpActionCompletion } from "@/components/checklists/FollowUpActionCompletion";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Spinner } from "@/components/ui/spinner";
import { useGetActionByIdQueryControllerGetById } from "@/generated/follow-up-actions/follow-up-actions";
import type { FollowUpActionResponseDto } from "@/generated/model";
import { offlineDb } from "@/lib/offline/db";

export const Route = createFileRoute(
  "/_authenticated/follow-up-actions/$actionId",
)({
  component: FollowUpActionDetailPage,
});

function FollowUpActionDetailPage() {
  const { actionId } = useParams({
    from: "/_authenticated/follow-up-actions/$actionId",
  });
  const navigate = useNavigate();

  const {
    data: actionData,
    isLoading,
    error,
  } = useGetActionByIdQueryControllerGetById(actionId);

  const action = actionData?.data;

  // Offline fallback: look up action from IndexedDB cache
  const [offlineAction, setOfflineAction] =
    useState<FollowUpActionResponseDto | null>(null);

  useEffect(() => {
    if (action || isLoading) return;

    async function loadFromCache() {
      // Search cached follow-up action lists for this actionId
      const allCaches = await offlineDb.followUpActions.toArray();
      for (const cache of allCaches) {
        const items = cache.items as FollowUpActionResponseDto[];
        const found = items.find((item) => item.id === actionId);
        if (found) {
          // Check if we have a local completion that overrides status
          const localCompletion =
            await offlineDb.followUpCompletions.get(actionId);
          if (localCompletion?.completedAt) {
            setOfflineAction({
              ...found,
              status: "COMPLETED" as FollowUpActionResponseDto["status"],
              completionNotes: localCompletion.completionNotes || undefined,
            });
          } else {
            setOfflineAction(found);
          }
          return;
        }
      }
    }

    loadFromCache();
  }, [action, isLoading, actionId]);

  const resolvedAction = action || offlineAction;

  const handleClose = () => {
    if (window.history.length > 1) {
      window.history.back();
      return;
    }
    // No history → pick a sensible fallback based on where the action came
    // from. If we somehow don't know the source yet (resolvedAction still
    // null), default to checklists.
    if (
      resolvedAction?.sourceType === "OUT_OF_RANGE_INCIDENT" &&
      resolvedAction.outOfRangeIncidentId
    ) {
      navigate({
        to: "/incidents/$incidentId",
        params: { incidentId: resolvedAction.outOfRangeIncidentId },
      });
      return;
    }
    navigate({ to: "/checklists" });
  };

  if (isLoading && !resolvedAction) {
    return (
      <div className="flex items-center justify-center py-12">
        <Spinner className="h-8 w-8 text-primary" />
      </div>
    );
  }

  if ((error && !offlineAction) || !resolvedAction) {
    return (
      <div className="space-y-4">
        <Button
          variant="ghost"
          size="sm"
          className="gap-2"
          onClick={handleClose}
        >
          <ArrowLeft className="h-4 w-4" />
          Back
        </Button>
        <Card>
          <CardContent className="py-8 text-center">
            <p className="text-muted-foreground">
              Follow-up action not found or you don&apos;t have access.
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <FollowUpActionCompletion action={resolvedAction} onClose={handleClose} />
  );
}
